import { useState, useEffect } from "react";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { FlaskConical, Play, Check, X, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SyntheticMonitoringPage() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);

  useEffect(() => { api.get("/synthetics").then((r) => { setTests(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const handleRun = async (testId) => {
    setRunning(testId);
    try {
      const { data } = await api.post(`/synthetics/${testId}/run`);
      setTests((prev) => prev.map((t) => t.test_id === testId ? { ...t, last_result: data.result, last_run: data.result.timestamp, status: data.result.passed ? "passing" : "failing" } : t));
      toast.success(data.result.passed ? "Test passed" : "Test failed");
    } catch { toast.error("Run failed"); }
    finally { setRunning(null); }
  };

  if (loading) return <div className="p-6"><Skeleton className="h-48 bg-white/5" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="synthetics-page">
      <div>
        <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Synthetic Monitoring</h1>
        <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>Automated tests for critical data workflows</p>
      </div>

      <div className="space-y-3" data-testid="synthetics-list">
        {tests.map((t, i) => {
          const passing = t.status === "passing";
          return (
            <div key={t.test_id} className="sf-card p-5 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`synthetic-${t.test_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: passing ? "rgba(45,106,79,0.08)" : "rgba(190,52,85,0.08)" }}>
                    <FlaskConical className="w-5 h-5" style={{ color: passing ? "#22C55E" : "#EF4444" }} />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-bold text-[#2D3142]">{t.name}</h3>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-[10px] font-mono" style={{ color: "#6B7280" }}>{t.type?.replace(/_/g, " ")}</span>
                      <span className="text-[10px] font-mono" style={{ color: "#6B7280" }}>{t.target}</span>
                      <span className="text-[10px] flex items-center gap-1" style={{ color: "#9CA3AF" }}><Clock className="w-3 h-3" />{t.schedule}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className="text-[9px] uppercase font-bold border-0 rounded-md" style={{ background: passing ? "rgba(45,106,79,0.12)" : "rgba(190,52,85,0.12)", color: passing ? "#4ADE80" : "#F87171" }}>{t.status}</Badge>
                  <Button size="sm" className="h-7 rounded-lg text-[10px] font-semibold text-[#2D3142] border-0" style={{ background: "rgba(30,58,95,0.15)", color: "#1E3A5F" }} onClick={() => handleRun(t.test_id)} disabled={running === t.test_id} data-testid={`run-synthetic-${t.test_id}`}>
                    {running === t.test_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                    Run Now
                  </Button>
                </div>
              </div>
              {t.last_result && (
                <div className="mt-3 flex items-center gap-4 text-[10px] font-mono" style={{ color: "#6B7280" }}>
                  <span className="flex items-center gap-1">{t.last_result.passed ? <Check className="w-3 h-3" style={{ color: "#2D6A4F" }} /> : <X className="w-3 h-3" style={{ color: "#BE3455" }} />}{t.last_result.passed ? "Passed" : "Failed"}</span>
                  <span>{t.last_result.latency_ms}ms</span>
                  <span>{t.last_run?.slice(0, 16).replace("T", " ")}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
